import React, { useEffect, useState } from "react"
import styles from "./index.module.css";
import { Button, Grid, Typography, InputBase,useMediaQuery } from '@material-ui/core';
import EmailIcon from '@material-ui/icons/Email';
import PhoneIcon from '@material-ui/icons/Phone';
import ProfileScreen from '../../../Profile';
import { useSelector } from 'react-redux';
import { IAppState } from '../../../../store';
import HttpHeader from '../../../../header';
import { Field, FieldProps, Form, Formik } from 'formik';
import { createStyles, Theme, withStyles } from "@material-ui/core/styles";
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import AddressPicker from '../../../AddressPicker/AddressPicker';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import { useSnackBar } from '../../../SnackBarContext/SnackBarContext';
import { SnackBarVariant } from '../../../SnackbarWrapper/SnackbarWrapper';
import { buildFieldRequiredMap, buildFieldVisibilityMap, formatGraphQLErrorMessage, defaultWebsiteBusinessFields, getFieldTitle, ICustomQuestion } from '../../../utils';
import { CustomQuestionsFields, ICustomFormAnswer } from '../../../CustomQuestionsFields/CustomQuestionsFields';
import { UPDATE_BUSINESS_CUSTOMER } from '../../../../graphql/user/updateUser';
import * as Yup from "yup";
import { BusinessCustomerStatus, UserRole } from '../../../../reducers/auth/types';
import { useTranslation } from "react-i18next";
import { TranslationKeys } from "../../../../utils/translations/types";
import { GET_CUSTOM_FORM_BY_TYPE } from "../../../../graphql/customers/getConsumerCustomForm";

const httpHeader = HttpHeader.getInstance();

const StyledInput = withStyles(() =>
  createStyles({
    root: {
      border: "1px solid #c0c0c0",
      borderRadius: 6,
      padding: "8px 10px",
      marginTop: 5,
      '&.Mui-focused': {
        borderColor: "var(--primary-color)"
      },
      '&.Mui-error': {
        borderColor: "#D13135",
        color: "#D13135"
      }
    },
  }),
)(InputBase);

const Account = () => {
  const snackbar = useSnackBar();
  const user = useSelector((state: IAppState) => state.authReducer.user);
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const {
    id: organisationId,
  } = website.organisation;
  const [editing, setEditing] = useState(false);
  const [requiredMap, setRequiredMap] = useState<Record<string, boolean>>(
    buildFieldRequiredMap(defaultWebsiteBusinessFields)
  );
  const [visibilityMap, setVisibilityMap] = useState<Record<string, boolean>>(
    buildFieldVisibilityMap(defaultWebsiteBusinessFields)
  );
  const [customQuestions, setCustomQuestions] = useState<ICustomQuestion[]>([]);
  const mdDown = useMediaQuery('(max-width: 900px)')
  const { t } = useTranslation()

  const validateCustomQuestions = (
    questions: ICustomQuestion[],
    answers: ICustomFormAnswer[]
  ) => {
    const requiredQuestions = questions.filter((q) => q.isRequired);
    const missingAnswers = requiredQuestions.filter((q) => {
      const questionId = q.id || `q_${q.questionText}`;
      const answer = answers.find((a) => a.questionId === questionId);
      return !answer || !answer.answer || answer.answer.trim() === "";
    });
    return missingAnswers;
  };

  const [updateBusinessCustomer] = useMutation(UPDATE_BUSINESS_CUSTOMER, {
    onCompleted: (data) => {
      setEditing(!editing);
      window.location.reload();
      snackbar({
        message: "Customer Information Saved",
        variant: SnackBarVariant.SUCCESS
      });
    },
    onError: (error: ApolloError) =>
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      })
  });

  const [getCustomFormByType] = useLazyQuery(GET_CUSTOM_FORM_BY_TYPE, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {

      if (data?.getConsumerCustomFormType?.config?.website?.fields.length) {
        const customMap = buildFieldRequiredMap(data.getConsumerCustomFormType.config.website.fields);
        const customVisibilityMap = buildFieldVisibilityMap(data.getConsumerCustomFormType.config.website.fields);
        setRequiredMap(customMap);
        setVisibilityMap(customVisibilityMap);
      } else {
        const defaultMap = buildFieldRequiredMap(defaultWebsiteBusinessFields);
        const defaultVisibilityMap = buildFieldVisibilityMap(defaultWebsiteBusinessFields);
        setRequiredMap(defaultMap);
        setVisibilityMap(defaultVisibilityMap);
      }
      const questions = data?.getConsumerCustomFormType?.config?.website?.customQuestions || [];
      setCustomQuestions(questions);
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
      const defaultMap = buildFieldRequiredMap(defaultWebsiteBusinessFields);
      const defaultVisibilityMap = buildFieldVisibilityMap(defaultWebsiteBusinessFields);
      setRequiredMap(defaultMap);
      setVisibilityMap(defaultVisibilityMap);
    }
  });

  useEffect(() => {
    if (user && user.businesscustomers) {
      getCustomFormByType({
        variables: {
          organisationId: organisationId,
          formType: "BUSINESS",
          atBranchLevel: false
        }
      })
    }
  }, [user])

  const getCountryFromLocaleString = (localeString: string) => {
    // var alpha2 = localeString.match("(^|[a-z])_[A-Z]{2}");
    const alpha2 = localeString.slice(-2);
    if (alpha2) {
      return alpha2;
    }
    return "GB"
  }

  const createCustomerValidationSchema = (
    requiredMap: Record<string, boolean>,
  ) => {
    const createConditionalString = (
      key: string,
      errorKey: TranslationKeys,
      interpolationValues?: Record<string, string | number>
    ) =>
      requiredMap[key]
        ? interpolationValues
          ? Yup.string().required(t(errorKey, { ...interpolationValues }))
          : Yup.string().required(t(errorKey))
        : Yup.string();

    const createConditionalEmail = (key: string, invalidErrorKey: TranslationKeys, requiredErrorKey: TranslationKeys,) => {
      const base = Yup.string().email(t(invalidErrorKey))
      return requiredMap[key] ? base.required(t(requiredErrorKey)) : base
    }

    const createConditionalPhone = (key: string, errorKey: TranslationKeys) => {
      const base = Yup.string().test("is-valid-phone", t("root:invalidPhoneNumber"), (value) =>
        !value ? true : isValidPhoneNumber(value)
      )
      return requiredMap[key] ? base.required(t(errorKey)) : base
    }

    const createAddressSchema = (required: boolean, errorKey: TranslationKeys) => {
      const shape = {
        fullAddress: required ? Yup.string().required(t(errorKey)) : Yup.string(),
        country: required ? Yup.string().required(t("validation:registeredAddressCountryRequired")) : Yup.string(),
        state: required ? Yup.string().required(t("validation:registeredAddressStateRequired")) : Yup.string(),
        city: required ? Yup.string().required(t("validation:tradingAddressCityRequired")) : Yup.string(),
        street: required ? Yup.string().required(t("validation:tradingAddressStreetRequired")) : Yup.string(),
      }

      return required ? Yup.object().shape(shape).required(t(errorKey)) : Yup.object().shape(shape)
    }

    return Yup.object().shape({
      businessName: createConditionalString('businessName', 'validation:businessNameRequired'),
      registrationNumber: createConditionalString('registrationNumber', 'validation:registrationNumberRequired'),
      VAT: createConditionalString('VAT', 'validation:vatRequired'),
      billing: Yup.object().shape({
        email: createConditionalEmail(
          'billingEmail',
          'validation:billingEmailInvalid',
          'validation:billingEmailRequired'
        ),
        phoneNumber: Yup.object().shape({
          phone: createConditionalPhone('billingPhoneNumber', 'validation:phoneNumberRequired')
        })
      }),
      registeredAddress: createAddressSchema(requiredMap.registeredAddress, 'validation:registeredAddressRequired'),
      tradingAddress: createAddressSchema(requiredMap.registeredAddress, 'validation:tradingAddressRequired'),
    })
  }

  const customerInfoSchema = createCustomerValidationSchema(requiredMap);
  const convertNotesToHTML = (note: string | undefined) => {
    if (typeof note !== 'string') {
      return note;
    }
    if (!note.includes('\n')) {
      return note;
    }
    const newNote = note.split('\n').reduce<JSX.Element[]>(
      (acc, line, index) => {
        acc.push(<React.Fragment key={index}>{line}</React.Fragment>);
        acc.push(<br key={`br-${index}`} />);
        return acc;
      }, []
    );
    return newNote
  };
  const HTMLdeclinedNote = convertNotesToHTML(user?.businesscustomers?.declinedReason);

  const businessNameTitle = getFieldTitle(
    t('root:businessName'),
    requiredMap.businessName
  );

  const registrationNumberTitle = getFieldTitle(
    t('root:registrationNumber'),
    requiredMap.registrationNumber
  );

  const VATRegistrationNumberTitle = getFieldTitle(
    t('root:taxVat'),
    requiredMap.VAT
  );

  const billingEmailTitle = getFieldTitle(
    t('root:billingEmail'),
    requiredMap.billingEmail
  );

  const billingPhoneNumberTitle = getFieldTitle(
    t('root:billingPhoneNo'),
    requiredMap.billingPhoneNumber
  );

  return (

    <div className={styles.column}>
      {user && user.customer && <ProfileScreen customer={user.customer} onAccount />}
      {user && user.businesscustomers && user.role !== UserRole.DRIVER &&
        <Formik
          initialValues={user.businesscustomers}
          validationSchema={customerInfoSchema}
          onSubmit={(values) => {
            const visibleQuestions = customQuestions.filter(
              (q) => q.visibleToBusinessCustomer !== false
            );
            const missingRequired = validateCustomQuestions(visibleQuestions, values.additionalQuestions || []);
            if (missingRequired.length > 0) {
              snackbar({
                message: `Please answer required questions: ${missingRequired.map((q) => q.questionText).join(", ")}`,
                variant: SnackBarVariant.ERROR
              });
              return;
            }
            const { approvedDrivers, status, declinedReason, businessCreditData, authorizedSignatories, ...rest } = values;
            const updatePayload = {
              ...rest,
              additionalQuestions: values.additionalQuestions || []
            };
            updateBusinessCustomer({
              variables: {
                businessCustomer: updatePayload,
                businessCustomerId: values.id
              }
            });
          }}
        >
          {(formikProps) => (
            <Form>
              <div className={mdDown? "flex col-flex margin-bottom padding-bottom":"flex cross-center margin-bottom padding-bottom"}>
                <div className="flex fill cross-center margin-bottom">
                  <div className={styles.avatar}>
                    <span className="font-large bold">{user.firstName.slice(0, 1).toUpperCase()}{user.lastName.slice(0, 1).toUpperCase()}</span>
                    {/* <img src="https://picsum.photos/200/200/?blur=10" /> */}
                  </div>
                  <div className="flex fill col-flex margin-left margin-right">
                    <Typography variant="h3" className="semi-bold capitalize" style={{ marginLeft: 1, marginBottom: 4 }}>{user.firstName} {user.lastName}</Typography>
                    <span className={mdDown ? "flex col-flex font-medium opacity50 " : "flex cross-center font-medium opacity50"}>
                      <div className="flex cross-center">
                      <EmailIcon style={{ fontSize: 17, marginRight: 3 }} />
                      <span>{user.email}</span>
                      </div>
                      <div className="flex cross-center" style={{marginTop: mdDown ? 5: 0}}>
                      <PhoneIcon style={{ fontSize: 17, marginRight: 3, marginLeft: mdDown ? 0 : 10}} />
                      <span className="">{user.phoneNumber.phone}</span>
                      </div>
                    </span>
                  </div>
                </div>
                <div className="flex cross-center" style={{marginLeft: mdDown? 5 : 0}}>
                  {!editing && <Button
                    onClick={() => {
                      setEditing(true)
                    }}
                    variant="outlined"
                    color="default" size="large"
                    disableRipple
                    style={{ textTransform: "initial", fontSize: 14, fontWeight: 500, padding: "6px 14px" }}
                  >
                    {t('misc:edit')}
                  </Button>}
                  <span className='margin-left'></span>
                  <Button
                    onClick={() => {
                      const lang = localStorage.getItem('lang');
                      httpHeader.resetHeaders();
                      localStorage.clear();
                      if (lang) {
                        localStorage.setItem('lang', lang);
                      }
                      window.location.replace("/");
                    }}
                    variant="outlined"
                    color="default" size="large"
                    disableRipple
                    style={{ textTransform: "initial", fontSize: 14, fontWeight: 500, padding: "6px 14px", whiteSpace: "nowrap"}}
                  >
                    {t('misc:logOut')}
                  </Button>
                </div>
              </div>
              <div className="flex cross-center margin-top">
                <Typography className="semi-bold">{t("root:businessAccount")}</Typography>
                <span className="flex fill margin-left" style={{ borderBottom: "1px solid #e9e9e9", marginTop: 4 }}></span>
              </div>
              <div className={styles.box}>
                {website.enableBusinessCustomerSignup && website.organisation.requiresCustomerVerification && (
                  <>
                  <Grid item xs={12} sm={12} md={12}>
                    <Typography variant="body1">
                      {t('root:verificationStatus')}: 
                      <span style={{
                        fontWeight: "bold", color: user?.businesscustomers?.status === BusinessCustomerStatus.PENDING ||
                        user?.businesscustomers?.status === BusinessCustomerStatus.DECLINED ? "red" : "green"
                      }}> {t<string>(`misc:status.${user?.businesscustomers?.status}`)}</span>
                    </Typography>
                  </Grid>
                  {user?.businesscustomers?.declinedReason && user.businesscustomers?.status === BusinessCustomerStatus.DECLINED && (
                    <Grid item xs={12} sm={12} md={12}>
                      <Typography variant="body1">
                        {t('root:note')}:
                        <span style={{ fontWeight: "bold" }}> {HTMLdeclinedNote} </span>
                      </Typography>
                    </Grid>
                  )}
                </>
                )}
                <Typography className="semi-bold padding-bottom--l">{t('root:companyInformation')}</Typography>
                <Grid container spacing={2}>
                  {visibilityMap.businessName && (
                  <Grid xs={6} md={3} item>
                    <span className="font-medium opacity50">{businessNameTitle}</span>
                    {!editing ? <Typography >{formikProps.values.businessName}</Typography> :
                      <Field name="businessName">
                        {({ field, meta: { touched, error }, }: FieldProps) => (
                          <>
                            <StyledInput
                              {...field}
                              placeholder= {t("root:enterBusinessName")}
                              fullWidth
                              error={touched && Boolean(error)}
                            />
                            <Typography variant="body2" color="error" className={styles.inputError}>{touched && Boolean(error) && error}</Typography>
                          </>
                        )}
                      </Field>}
                  </Grid>
                  )}
                  {visibilityMap.registrationNumber && (
                  <Grid xs={6} md={3} item>
                    <span className="font-medium opacity50">{registrationNumberTitle}</span>
                    {!editing ? <Typography >{formikProps.values.registrationNumber}</Typography> :
                      <Field name="registrationNumber">
                        {({ field, meta: { touched, error }, }: FieldProps) => (
                          <>
                            <StyledInput
                              {...field}
                              placeholder= {t('root:registrationNumberPlaceholder')}
                              fullWidth
                              error={touched && Boolean(error)}
                            />
                            <Typography variant="body2" color="error" className={styles.inputError}>{touched && Boolean(error) && error}</Typography>
                          </>
                        )}
                      </Field>}
                  </Grid>
                  )}
                  {visibilityMap.VAT && (
                  <Grid xs={12} md={6} item>
                    <span className="font-medium opacity50">{VATRegistrationNumberTitle}</span>
                    {!editing ? <Typography >{formikProps.values.VAT}</Typography> :
                      <Field name="VAT">
                        {({ field, meta: { touched, error }, }: FieldProps) => (
                          <>
                            <StyledInput
                              {...field}
                              placeholder= {t('root:vatPlaceholder')}
                              fullWidth
                              error={touched && Boolean(error)}
                            />
                            <Typography variant="body2" color="error" className={styles.inputError}>{touched && Boolean(error) && error}</Typography>
                          </>
                        )}
                      </Field>}
                  </Grid>
                  )}
                  {visibilityMap.registeredAddress && (
                  <Grid xs={12} md={6} item>
                    <span className="font-medium opacity50" style={{ display: 'inline-block', marginBottom: 4 }}>{t('root:registeredAddress')}</span>
                    {!editing ? <Typography >{formikProps.values.registeredAddress.fullAddress}</Typography> :
                      <AddressPicker
                        fieldName="registeredAddress"
                        required={requiredMap.registeredAddress}
                        onChange={(address) => {
                          if (address) {
                            formikProps.setFieldValue("registeredAddress", address)
                          }
                        }}
                      />}
                  </Grid>
                  )}
                  {visibilityMap.tradingAddress && (
                  <Grid xs={12} md={6} item>
                    <span className="font-medium opacity50"style={{ display: 'inline-block', marginBottom: 4 }}>
                      <span >{t('root:tradingAddress')}</span>
                      {editing && <span onClick={() => {
                        formikProps.setFieldValue("tradingAddress", formikProps.values.registeredAddress)
                        formikProps.setFieldValue("tradingAddressSame", true)
                        }} 
                        className='margin-left' style={{ color: "var(--theme-primary)", textDecoration: "underline", cursor: "pointer" }}>{t("root:sameAsRegisteredAddress")}</span>}
                    </span>
                    {!editing ? <Typography>{formikProps.values.tradingAddress.fullAddress}</Typography> :
                      <AddressPicker
                        fieldName="tradingAddress"
                        required={requiredMap.tradingAddress}
                        onChange={(address) => {
                          if (address) {
                            formikProps.setFieldValue("tradingAddress", address)
                            formikProps.setFieldValue("tradingAddressSame", false)
                          }
                        }}
                      />}
                  </Grid>
                  )}
                </Grid>
              </div>
              <div className={styles.box}>
                <Typography className="semi-bold padding-bottom--l">{t("root:billingInformation")}</Typography>
                <Grid container spacing={2} direction={mdDown ? "column" : "row"}>
                  {visibilityMap.billingPhoneNumber && (
                  <Grid xs={10} sm={8} md={4} item>
                    <span className="font-medium opacity50">{billingPhoneNumberTitle}</span>
                    {!editing ? <Typography >{formikProps.values.billing.phoneNumber.phone}</Typography> :
                      <Field
                        component={StyledInput}
                        name={"billing.phoneNumber.phone"}
                        required
                      >
                        {({ field, meta: { touched, error } }: FieldProps) => {
                          return (
                            <div className={`phone-input-container`} style={{ marginTop: 5 }}>
                              <div style={{ border: `1px solid ${(touched && Boolean(error)) ? '#D13135' : '#c0c0c0'}`, borderRadius: 6 }}>
                                <PhoneInput
                                  {...field}
                                  international={false}
                                  defaultCountry={getCountryFromLocaleString(website.organisation.locale)}   //
                                  placeholder={t("root:phonePlaceholder")}
                                  className={(touched && Boolean(error)) ? "PhoneInputInputError" : ""}
                                  name={"billing.phoneNumber.phone"}
                                  onChange={(val) => {
                                    formikProps.setFieldValue('billing.phoneNumber.phone', val)
                                  }}
                                  onCountryChange={(val) => {
                                    formikProps.setFieldValue('billing.phoneNumber.country', val)
                                  }}
                                />
                              </div>
                              <Typography variant="body2" color="error" className={styles.inputError}>{touched && Boolean(error) && error}</Typography>
                            </div>
                          );
                        }}
                      </Field>}
                  </Grid>
                  )}
                  {visibilityMap.billingEmail && (
                  <Grid xs={10} sm={8} md={4} item>
                    <span className="font-medium opacity50">{billingEmailTitle}</span>
                    {!editing ? <Typography >{formikProps.values.billing.email}</Typography> :
                      <Field name="billing.email" >
                        {({ field, meta: { touched, error }, }: FieldProps) => (
                          <>
                            <StyledInput
                              {...field}
                              placeholder= {t('root:emailPlaceholder')}
                              fullWidth
                              error={touched && Boolean(error)}
                            />
                            <Typography variant="body2" color="error" className={styles.inputError}>{touched && Boolean(error) && error}</Typography>
                          </>
                        )}
                      </Field>}
                  </Grid>
                  )}
                </Grid>
              </div>
              {customQuestions.length > 0 && (
                <div className={styles.box}>
                  <CustomQuestionsFields
                    customQuestions={customQuestions}
                    answers={formikProps.values.additionalQuestions || []}
                    onChange={(answers: ICustomFormAnswer[]) =>
                      formikProps.setFieldValue("additionalQuestions", answers)}
                    readOnly={!editing}
                  />
                </div>
              )}
              {editing && <div style={{ marginTop: 30 }}>
                <Button
                  onClick={() => {
                    setEditing(false);
                    formikProps.resetForm();
                  }}
                  variant="outlined"
                  color="default" size="large"
                  disableRipple
                  style={{ textTransform: "initial", fontSize: 14, fontWeight: 500, padding: "6px 14px" }}
                >
                  {t('misc:cancel')}
                  </Button>
                <span className="margin-left"></span>
                <Button
                  type="submit"
                  variant="contained"
                  style={{
                    background: "var(--primary-color)",
                    textTransform: "initial",
                    boxShadow: "none",
                    fontSize: 14,
                    fontWeight: 500,
                    color: "#fff"
                  }}
                  size="large"
                  disableRipple
                >
                  {t('misc:save')}
                  </Button>
              </div>}
            </Form>
          )}
        </Formik>
      }
    </div>
  )
}

export default Account